import React, { useEffect, useMemo, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { storagePathService } from '../../services/storage-path.service'
import { StoragePath } from '../../types'
import './StoragePathVisualizationDialog.scss'

interface StoragePathVisualizationDialogProps {
  onClose: () => void
}

interface FolderNode {
  name: string
  fullPath: string
  storagePaths: StoragePath[]
  documents: Array<{ id: number; title: string }>
  children: Map<string, FolderNode>
}

type ViewMode = 'templates' | 'actual'

interface TreeNodeProps {
  node: FolderNode
  level: number
  expanded: Set<string>
  onToggleExpand: (path: string) => void
  viewMode: ViewMode
  t: (key: string) => string
}

const TreeNode: React.FC<TreeNodeProps> = ({ node, level, expanded, onToggleExpand, viewMode, t }) => {
  const isExpanded = expanded.has(node.fullPath)
  const hasChildren = node.children.size > 0
  const hasStoragePaths = node.storagePaths.length > 0
  const hasDocuments = node.documents.length > 0
  const isPlaceholder = node.name.includes('{{') && node.name.includes('}}')
  const isEmpty = !hasChildren && !hasStoragePaths && !hasDocuments

  const handleFolderClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (hasChildren || hasStoragePaths || hasDocuments) {
      onToggleExpand(node.fullPath)
    }
  }

  const getFolderIcon = () => {
    if (isEmpty) {
      return 'bi-folder'
    }
    return isExpanded ? 'bi-folder2-open' : 'bi-folder2'
  }

  const getFolderClassName = () => {
    let className = 'storage-path-tree-folder'
    if (isEmpty) {
      className += ' storage-path-tree-folder-empty'
    } else if (isPlaceholder && viewMode === 'templates') {
      className += hasStoragePaths ? ' storage-path-tree-folder-placeholder-with-paths' : ' storage-path-tree-folder-placeholder'
    } else {
      className += hasStoragePaths || hasDocuments ? ' storage-path-tree-folder-with-content' : ' storage-path-tree-folder-empty'
    }
    return className
  }

  const countDocumentsRecursive = (node: FolderNode): number => {
    let count = node.documents.length
    node.children.forEach((child) => {
      count += countDocumentsRecursive(child)
    })
    return count
  }

  const totalDocumentsCount = countDocumentsRecursive(node)

  return (
    <div className="storage-path-tree-node">
      <div className={getFolderClassName()} style={{ paddingLeft: `${level * 20 + 8}px` }} onClick={handleFolderClick}>
        <div className="storage-path-tree-folder-content">
          {hasChildren || hasStoragePaths || hasDocuments ? (
            <i className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} storage-path-tree-chevron`}></i>
          ) : (
            <span className="storage-path-tree-chevron-spacer"></span>
          )}
          <i className={`bi ${getFolderIcon()} storage-path-tree-folder-icon`}></i>
          <span className="storage-path-tree-folder-name">{node.name}</span>
          {viewMode === 'templates' && hasStoragePaths && <span className="storage-path-tree-folder-count">({node.storagePaths.length})</span>}
          {viewMode === 'actual' && totalDocumentsCount > 0 && <span className="storage-path-tree-folder-count">({totalDocumentsCount})</span>}
        </div>
      </div>

      {isExpanded && (
        <div className="storage-path-tree-children">
          {Array.from(node.children.values()).map((child) => (
            <TreeNode
              key={child.fullPath}
              node={child}
              level={level + 1}
              expanded={expanded}
              onToggleExpand={onToggleExpand}
              viewMode={viewMode}
              t={t}
            />
          ))}

          {viewMode === 'actual' && hasDocuments && (
            <div className="storage-path-tree-documents">
              {node.documents.map((document) => {
                const handleDocumentClick = (e: React.MouseEvent) => {
                  e.stopPropagation()
                  const url = `/documents/${document.id}/details`
                  window.open(url, '_blank')
                }

                return (
                  <div
                    key={document.id}
                    className="storage-path-tree-document"
                    style={{ paddingLeft: `${(level + 1) * 20 + 8}px` }}
                    onClick={handleDocumentClick}
                  >
                    <div className="storage-path-tree-document-content">
                      <i className="bi bi-file-earmark-fill storage-path-tree-document-icon"></i>
                      <span className="storage-path-tree-document-name" title={document.title}>
                        {document.title.length > 50 ? `${document.title.slice(0, 47)}...` : document.title}
                      </span>
                      <i className="bi bi-search storage-path-tree-document-preview-icon"></i>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export const StoragePathVisualizationDialog: React.FC<StoragePathVisualizationDialogProps> = ({ onClose }) => {
  const { t } = useTranslation()
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>('templates')
  const [storagePaths, setStoragePaths] = useState<StoragePath[]>([])
  const [folderStructure, setFolderStructure] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['/']))
  const [searchQuery, setSearchQuery] = useState<string>('')

  useEffect(() => {
    loadData()
  }, [viewMode])

  const loadData = async () => {
    setLoading(true)
    try {
      if (viewMode === 'templates') {
        const response = await storagePathService.listAll()
        setStoragePaths(response.results)
        setFolderStructure([])
      } else {
        const structure = await storagePathService.getFolderStructure()
        setFolderStructure(structure)
        setStoragePaths([])
      }
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const buildFolderTreeFromTemplates = (paths: StoragePath[]): FolderNode => {
    const root: FolderNode = {
      name: '/',
      fullPath: '/',
      storagePaths: [],
      documents: [],
      children: new Map(),
    }

    paths.forEach((storagePath) => {
      const path = storagePath.path || ''
      const parts = path.split('/').filter((part) => part.length > 0)

      let current = root
      let currentPath = ''

      parts.forEach((part, index) => {
        currentPath = currentPath ? `${currentPath}/${part}` : `/${part}`

        if (!current.children.has(part)) {
          current.children.set(part, {
            name: part,
            fullPath: currentPath,
            storagePaths: [],
            documents: [],
            children: new Map(),
          })
        }

        current = current.children.get(part)!

        if (index === parts.length - 1) {
          current.storagePaths.push(storagePath)
        }
      })
    })

    return root
  }

  const buildFolderTreeFromStructure = (structure: any[], searchQuery: string = ''): FolderNode => {
    const root: FolderNode = {
      name: '/',
      fullPath: '/',
      storagePaths: [],
      documents: [],
      children: new Map(),
    }

    const matchesSearch = (name: string, query: string): boolean => {
      if (!query) return true
      return name.toLowerCase().includes(query.toLowerCase())
    }

    const hasDescendantMatch = (item: any, query: string): boolean => {
      if (matchesSearch(item.name, query)) return true
      if (item.children && item.children.length > 0) {
        return item.children.some((child: any) => hasDescendantMatch(child, query))
      }
      return false
    }

    const buildNode = (item: any, current: FolderNode, currentPath: string, parentMatches: boolean = false): boolean => {
      const nodePath = currentPath ? `${currentPath}/${item.name}` : `/${item.name}`
      const itemMatches = matchesSearch(item.name, searchQuery)
      const shouldShowAllChildren = parentMatches || itemMatches

      let shouldInclude = true

      if (searchQuery) {
        if (itemMatches || parentMatches) {
          shouldInclude = true
        } else {
          shouldInclude = hasDescendantMatch(item, searchQuery)
        }
      }

      if (!shouldInclude) return false

      if (!current.children.has(item.name)) {
        current.children.set(item.name, {
          name: item.name,
          fullPath: nodePath,
          storagePaths: [],
          documents: item.documents || [],
          children: new Map(),
        })
      }

      const node = current.children.get(item.name)!

      if (item.children && item.children.length > 0) {
        item.children.forEach((child: any) => {
          if (shouldShowAllChildren) {
            buildNode(child, node, nodePath, true)
          } else {
            buildNode(child, node, nodePath, false)
          }
        })
      }

      return itemMatches || parentMatches
    }

    structure.forEach((item) => {
      buildNode(item, root, '', false)
    })

    return root
  }

  const tree = useMemo(() => {
    if (loading) {
      return null
    }

    if (viewMode === 'templates') {
      if (storagePaths.length === 0) {
        return null
      }
      return buildFolderTreeFromTemplates(storagePaths)
    } else {
      if (folderStructure.length === 0) {
        return null
      }
      return buildFolderTreeFromStructure(folderStructure, searchQuery)
    }
  }, [storagePaths, folderStructure, loading, viewMode, searchQuery])

  const handleToggleExpand = (path: string) => {
    setExpanded((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(path)) {
        newSet.delete(path)
      } else {
        newSet.add(path)
      }
      return newSet
    })
  }

  const getAllExpandablePaths = (node: FolderNode): Set<string> => {
    const allPaths = new Set<string>(['/'])
    const collectPaths = (n: FolderNode) => {
      if (n.children.size > 0 || n.storagePaths.length > 0 || n.documents.length > 0) {
        allPaths.add(n.fullPath)
        n.children.forEach((child) => collectPaths(child))
      }
    }
    collectPaths(node)
    return allPaths
  }

  const isAllExpanded = () => {
    if (!tree) return false
    const allPaths = getAllExpandablePaths(tree)
    return allPaths.size === expanded.size && Array.from(allPaths).every((path) => expanded.has(path))
  }

  const handleToggleExpandAll = () => {
    if (!tree) return

    if (isAllExpanded()) {
      setExpanded(new Set(['/']))
    } else {
      const allPaths = getAllExpandablePaths(tree)
      setExpanded(allPaths)
    }
  }

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen)
  }

  const allExpanded = isAllExpanded()

  return (
    <Modal
      show={true}
      onHide={onClose}
      size="xl"
      fullscreen={isFullscreen ? true : undefined}
      className="storage-path-visualization-dialog"
      backdropClassName="storage-path-visualization-dialog-backdrop"
    >
      <Modal.Header>
        <Modal.Title>
          <i className="bi bi-diagram-3 me-2"></i>
          {t('storage_path_visualization')}: {t('storage_paths')}
        </Modal.Title>
        <div className="d-flex align-items-center gap-2 ms-auto">
          <Button
            variant="link"
            size="sm"
            onClick={toggleFullscreen}
            className="p-1"
            title={isFullscreen ? t('exit_fullscreen') || 'Izađi iz punog ekrana' : t('fullscreen') || 'Puni ekran'}
            style={{ minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <i className={`bi ${isFullscreen ? 'bi-fullscreen-exit' : 'bi-fullscreen'}`}></i>
          </Button>
          <Button
            variant="link"
            size="sm"
            onClick={onClose}
            className="p-1"
            title={t('close') || 'Zatvori'}
            style={{ minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <i className="bi bi-x-lg"></i>
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body style={{ height: isFullscreen ? 'calc(100vh - 80px)' : '70vh', padding: 0, display: 'flex', flexDirection: 'column' }}>
        <div className="storage-path-visualization-toolbar">
          <div className="btn-group" role="group">
            <input
              type="radio"
              className="btn-check"
              name="viewMode"
              id="viewModeTemplates"
              checked={viewMode === 'templates'}
              onChange={() => {
                setViewMode('templates')
                setSearchQuery('')
              }}
            />
            <label htmlFor="viewModeTemplates" className="btn btn-outline-primary btn-sm">
              {t('template_paths')}
            </label>
            <input
              type="radio"
              className="btn-check"
              name="viewMode"
              id="viewModeActual"
              checked={viewMode === 'actual'}
              onChange={() => setViewMode('actual')}
            />
            <label htmlFor="viewModeActual" className="btn btn-outline-primary btn-sm">
              {t('created_folders')}
            </label>
          </div>
          {viewMode === 'actual' && (
            <div className="storage-path-search-input-wrapper">
              <i className="bi bi-search storage-path-search-icon"></i>
              <input
                type="text"
                className="form-control form-control-sm storage-path-search-input"
                placeholder={t('search_folders')}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  if (e.target.value) {
                    const allPaths = tree ? getAllExpandablePaths(tree) : new Set(['/'])
                    setExpanded(allPaths)
                  }
                }}
              />
              {searchQuery && (
                <button
                  className="btn btn-link btn-sm p-0 storage-path-search-clear"
                  onClick={() => {
                    setSearchQuery('')
                    setExpanded(new Set(['/']))
                  }}
                  title={t('clear')}
                >
                  <i className="bi bi-x"></i>
                </button>
              )}
            </div>
          )}
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={handleToggleExpandAll}
            title={allExpanded ? t('collapse_all') : t('expand_all')}
          >
            <i className={`bi ${allExpanded ? 'bi-arrows-angle-contract' : 'bi-arrows-angle-expand'} me-1`}></i>
            {allExpanded ? t('collapse_all') : t('expand_all')}
          </button>
        </div>
        <div className="storage-path-visualization-tree">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
              <div className="text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">{t('loading')}</span>
                </div>
                <div className="mt-2">{t('loading')}</div>
              </div>
            </div>
          ) : !tree || (tree.children.size === 0 && tree.storagePaths.length === 0 && tree.documents.length === 0) ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
              <div className="text-center text-muted">
                <i className="bi bi-folder-x" style={{ fontSize: '3rem' }}></i>
                <div className="mt-3">{viewMode === 'templates' ? t('no_storage_paths') : t('no_folders')}</div>
              </div>
            </div>
          ) : (
            <TreeNode node={tree} level={0} expanded={expanded} onToggleExpand={handleToggleExpand} viewMode={viewMode} t={t} />
          )}
        </div>
      </Modal.Body>
    </Modal>
  )
}
